import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Printer, RotateCcw, Check, ListChecks,
  Package, MapPin, Camera, Wifi, Radio,
} from "lucide-react";

/* One-page, printable pre-show checklist for travelling producers.
   On screen: interactive tick boxes. On print: clean monochrome “paper”
   (app chrome is hidden via #fc-print-root rules in index.css). */

const SECTIONS = [
  {
    id: "pack",
    title: "Pack the kit (before you leave)",
    icon: Package,
    items: [
      "Production laptop + charger",
      "Owl 3 camera + its USB cable",
      "2× USB microphones",
      "Lav mic(s) + spare batteries",
      "USB-C hub + spare cables",
      "Headphones (one per host)",
      "Verizon 5G hotspot — charged",
      "Phone (optional guest camera) + cable",
      "Clip light + gaffer tape (optional)",
    ],
  },
  {
    id: "arrive",
    title: "On arrival — 15-minute setup",
    icon: MapPin,
    items: [
      "Find power; plug in the laptop and hub",
      "Get online: venue Wi-Fi or 5G hotspot",
      "Open the Studio and confirm it loads",
      "Place the camera (Owl centre / webcam at eye level)",
      "Place the mics (~12in per host, or Owl in the centre)",
      "Light on faces, not behind them",
      "Tidy the background; tape down loose cables",
    ],
  },
  {
    id: "check",
    title: "Camera & audio check",
    icon: Camera,
    items: [
      "Select the camera in the setup screen; check the preview",
      "Select the mic; say a few words and watch the level",
      "Everyone on headphones (no echo)",
      "Record a 20-second test and play it back",
    ],
  },
  {
    id: "internet",
    title: "Internet & go-live readiness",
    icon: Wifi,
    items: [
      "Run a short test broadcast",
      "Pick the strongest connection (Wi-Fi vs 5G hotspot)",
      "Close other apps and downloads",
      "Confirm the live destination is selected",
      "Ready to drop to 720p if video looks choppy",
    ],
  },
  {
    id: "showtime",
    title: "Showtime",
    icon: Radio,
    items: [
      "Name badges printed & on the welcome table",
      "Cameras placed (Owl centre; wide-angle as a 2nd feed if using)",
      "Guests admitted from the Green Room",
      "Name plates ready (members auto-fill; type in guests)",
      "Recording armed (or the Record tab ready)",
      "Go live — confirm the status shows ON AIR",
      "Watch the status cards through the show",
    ],
  },
];

function Field({ label }) {
  return (
    <div className="flex items-end gap-2">
      <span className="shrink-0 text-sm font-medium text-foreground">{label}:</span>
      <span className="h-4 flex-1 border-b border-dashed border-muted-foreground/50" />
    </div>
  );
}

export default function PreShowChecklist() {
  const [checked, setChecked] = useState({});

  const allItems = useMemo(
    () => SECTIONS.flatMap((s) => s.items.map((_, i) => `${s.id}-${i}`)),
    []
  );
  const doneCount = allItems.filter((id) => checked[id]).length;

  const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));
  const reset = () => setChecked({});

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      {/* Toolbar — hidden when printing */}
      <div className="fc-print-hide flex flex-wrap items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/portal/guides/producer-setup" data-testid="checklist-back-link">
            <ArrowLeft className="mr-2 h-4 w-4" /> Producer setup
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <span className="mr-1 text-sm text-muted-foreground tabular-nums" data-testid="checklist-progress">
            {doneCount}/{allItems.length} done
          </span>
          <Button variant="outline" size="sm" onClick={reset} data-testid="checklist-reset-btn">
            <RotateCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          <Button size="sm" onClick={() => window.print()} data-testid="checklist-print-btn">
            <Printer className="mr-2 h-4 w-4" /> Print / Save PDF
          </Button>
        </div>
      </div>

      {/* Printable sheet */}
      <div
        id="fc-print-root"
        data-testid="checklist-sheet"
        className="rounded-2xl border border-border bg-card p-6 shadow-sm fc-paper-noise sm:p-8"
      >
        <header className="mb-6 border-b border-border pb-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">Friday Conversations</p>
              <h1 className="mt-1 font-serif text-2xl font-semibold text-foreground">Pre-Show Producer Checklist</h1>
              <p className="mt-1 text-sm text-muted-foreground">The travel playbook — set up at any venue and go live with confidence.</p>
            </div>
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <ListChecks className="h-6 w-6" />
            </div>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
            <Field label="Venue" />
            <Field label="Date" />
            <Field label="Producer" />
            <Field label="Show / Episode" />
          </div>
        </header>

        <div className="grid gap-x-10 gap-y-7 sm:grid-cols-2">
          {SECTIONS.map((section) => {
            const SIcon = section.icon;
            return (
              <section key={section.id} className="break-inside-avoid space-y-2.5">
                <div className="flex items-center gap-2">
                  <SIcon className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground">{section.title}</h2>
                </div>
                <ul className="space-y-0.5">
                  {section.items.map((label, i) => {
                    const id = `${section.id}-${i}`;
                    const isChecked = !!checked[id];
                    return (
                      <li key={id}>
                        <button
                          type="button"
                          onClick={() => toggle(id)}
                          aria-pressed={isChecked}
                          data-testid={`checklist-item-${id}`}
                          className="flex w-full items-start gap-3 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <span
                            className={`fc-checkbox mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border-2 transition-colors ${
                              isChecked
                                ? "fc-checkbox-checked border-primary bg-primary text-primary-foreground"
                                : "border-muted-foreground/45"
                            }`}
                          >
                            {isChecked && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                          </span>
                          <span className={`text-sm leading-relaxed ${isChecked ? "text-muted-foreground line-through" : "text-foreground"}`}>
                            {label}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>

        <footer className="mt-7 border-t border-border pt-4 text-center text-xs text-muted-foreground">
          Friday Conversations · Producer pre-show checklist · fridayconversations.live
        </footer>
      </div>
    </div>
  );
}
